import {ISettingsSetter} from '@wix/tpa-settings'
import settingsParams from '../../components/widget/settingsParams'
import {ALL_CATEGORIES_FILTER} from '../constants/settings'

export interface CategoryWithSelection {
  guid: string
  selected: boolean
}

const ENCODING_CHARS = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz!#$%&()*+-;<=>?@^_`{|}~,.[]'

const convertHexStringToBytes = (hex: string): number[] =>
  Array.from({length: hex.length / 2}, (_, index) => {
    const startIndex = index * 2
    return parseInt(hex.substring(startIndex, startIndex + 2), 16)
  })

const convertBytesToBigInt = (bytes: number[]): bigint =>
  bytes.reduce((result, byte) => (result << BigInt(8)) + BigInt(byte), BigInt(0))

const convertBigIntToBase89 = (value: bigint): string => {
  if (value === BigInt(0)) {
    return '0'.padStart(20, '0')
  }

  const base = BigInt(ENCODING_CHARS.length)
  const digits: number[] = []
  let temp = value

  while (temp > BigInt(0)) {
    digits.unshift(Number(temp % base))
    temp = temp / base
  }

  return digits
    .map(digit => ENCODING_CHARS[digit])
    .join('')
    .padStart(20, '0')
}

const convertBase89ToBigInt = (encoded: string): bigint => {
  const base = BigInt(ENCODING_CHARS.length)
  return Array.from(encoded).reduce((result, char) => {
    const digitValue = ENCODING_CHARS.indexOf(char)
    return result * base + BigInt(digitValue)
  }, BigInt(0))
}

const convertBigIntToBytes = (value: bigint): number[] =>
  Array.from({length: 16}, (_, index) => {
    const shiftAmount = BigInt((15 - index) * 8)
    return Number((value >> shiftAmount) & BigInt(0xff))
  })

const convertBytesToHexString = (bytes: number[]): string =>
  bytes
    .slice(0, 16)
    .map(byte => byte.toString(16).padStart(2, '0'))
    .join('')

const formatAsGuid = (hexString: string): string =>
  [
    hexString.substring(0, 8),
    hexString.substring(8, 12),
    hexString.substring(12, 16),
    hexString.substring(16, 20),
    hexString.substring(20, 32),
  ].join('-')

export const encodeCategoryState = (guid: string, selected: boolean): string => {
  if (guid === ALL_CATEGORIES_FILTER) {
    let guidBigInt = BigInt(0)
    if (selected) {
      guidBigInt = guidBigInt + (BigInt(1) << BigInt(128))
    }
    return convertBigIntToBase89(guidBigInt)
  }

  const guidWithoutDashes = guid.replace(/-/g, '')
  const hexBytes = convertHexStringToBytes(guidWithoutDashes)
  let guidBigInt = convertBytesToBigInt(hexBytes)

  if (guidBigInt === BigInt(0)) {
    guidBigInt = BigInt(1)
  }

  if (selected) {
    guidBigInt = guidBigInt + (BigInt(1) << BigInt(128))
  }

  return convertBigIntToBase89(guidBigInt)
}

export const decodeCategoryState = (encoded: string): CategoryWithSelection => {
  let bigIntValue = convertBase89ToBigInt(encoded)
  const selectedFlag = bigIntValue >= BigInt(1) << BigInt(128)

  if (selectedFlag) {
    bigIntValue = bigIntValue - (BigInt(1) << BigInt(128))
  }

  if (bigIntValue === BigInt(0)) {
    return {guid: ALL_CATEGORIES_FILTER, selected: selectedFlag}
  }

  if (bigIntValue === BigInt(1)) {
    bigIntValue = BigInt(0)
  }

  const guidBytes = convertBigIntToBytes(bigIntValue)
  const hexString = convertBytesToHexString(guidBytes)
  const guid = formatAsGuid(hexString)

  return {guid, selected: selectedFlag}
}

export const setCategoriesWithSelection = (categories: CategoryWithSelection[], setSetting: ISettingsSetter) => {
  const encodedIds = categories.map(category => encodeCategoryState(category.guid, category.selected)).join('')
  setSetting(settingsParams.selectedCategoryIds, encodedIds)
}
